import jsPDF from "jspdf";
import "jspdf-autotable";
import beReliableLogo from "../assets/logo/bereliable-logo.png";

export const COMPANY_LOGOS = {
  "TATA AIG": "/assets/partners/tata-aig.png",
  "GO DIGIT": "/assets/partners/go-digit.png",
  "BAJAJ ALLIANZ": "/assets/partners/bajaj-allianz.png",
  "ICICI LOMBARD": "/assets/partners/icici-lombard.png",
  "HDFC ERGO": "/assets/partners/hdfc-ergo.png",
  "NEW INDIA ASSURANCE": "/assets/partners/the-new-assurance.png",
};

const formatCurrency = (value) => `${(Number(value) || 0).toLocaleString("en-IN")}`;

const loadLogo = async (src, targetHeight) => {
  const img = new Image();
  img.crossOrigin = "anonymous";

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = src;
  });

  const calculatedWidth = (img.width / img.height) * targetHeight;
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.7), width: calculatedWidth };
};

export const generateQuotationPDF = async (quotationData = {}, options = {}) => {
  const formData = quotationData || {};
  const companyLogos = options.companyLogos || COMPANY_LOGOS;

  const selectedCompany = formData.insuranceCompany;
  const selectedCompanyLogo = selectedCompany ? companyLogos[selectedCompany] : undefined;

  const beLogoWidth = 135;
  const beLogoHeight = 35;
  const insurerLogoHeight = 37;

  let companyLogoData = null;
  let calculatedInsurerLogoWidth = 0;
  if (selectedCompanyLogo) {
    try {
      const logo = await loadLogo(selectedCompanyLogo, insurerLogoHeight);
      companyLogoData = logo.dataUrl;
      calculatedInsurerLogoWidth = logo.width;
    } catch (err) {
      console.warn("Could not load company logo:", err);
    }
  }

  const doc = new jsPDF("p", "pt", "a4");
  doc.setFont("helvetica", "normal");

  const pageWidth = 595;
  const pageHeight = 842;
  const marginTop = 20;
  const marginBottom = 20;
  const marginLeft = 25;
  const marginRight = 25;
  const contentWidth = 545;

  let yPos = marginTop;

  const V = formData.vehicleDetails || {};
  const I = formData.idvDetails || {};
  const P = formData.premiumDetails || {};
  const D = formData.discounts || {};
  const policyType = V.policyType || "";

  const totalOwnDamagePremium = (P.subTotalBasic || 0) - (D.ncbAmount || 0);
  const netOwnDamagePremium = totalOwnDamagePremium + (formData.addon_amount || 0);

  const basicOD = formData.od_final || 0;
  const elecAcc = P.elecAccessories || 0;
  const nonElecAcc = P.nonElecAccessories || 0;
  const biFuelKitOD = P.biFuelKit || 0;
  const subTotalBasic = P.subTotalBasic || 0;
  const antiTheft = P.discountAntiTheft || 0;
  const ncbAmount = D.ncbAmount || 0;
  const subTotalDiscounts = P.subTotalDiscounts || 0;
  const totalODPremium = totalOwnDamagePremium || 0;
  const rsaAmount = 0;
  const addOnAmount = (formData.addon_amount || 0) + rsaAmount;
  const netODPremium = netOwnDamagePremium || 0;

  const isThirdPartyOnly = policyType === "THIRD PARTY";
  const isOwnDamageOnly = policyType === "OWN DAMAGE ONLY";

  const odValues = isThirdPartyOnly
    ? {
        basicOD: 0,
        elecAcc: 0,
        nonElecAcc: 0,
        biFuelKitOD: 0,
        subTotalBasic: 0,
        antiTheft: 0,
        ncbAmount: 0,
        subTotalDiscounts: 0,
        totalODPremium: 0,
        addOnAmount: 0,
        netODPremium: 0,
      }
    : {
        basicOD,
        elecAcc,
        nonElecAcc,
        biFuelKitOD,
        subTotalBasic,
        antiTheft,
        ncbAmount,
        subTotalDiscounts,
        totalODPremium,
        addOnAmount,
        netODPremium,
      };

  const ccRawPdf = V.cubicCapacity;
  const ccPdf = Number((ccRawPdf || "").toString().trim());
  let thirdPartyBase = 0;
  if (!ccPdf || ccPdf === 0 || isNaN(ccPdf)) {
    thirdPartyBase = 0;
  } else if (ccPdf <= 1000) {
    thirdPartyBase = 2094;
  } else if (ccPdf <= 1500) {
    thirdPartyBase = 3416;
  } else {
    thirdPartyBase = 7897;
  }

  const paOwner =
    Number(formData.liab_pa_owner ?? formData.paOwnerDriver) || 0;
  const paPassenger =
    Number(formData.liab_pa_passenger ?? formData.paUnnamedPassenger) || 0;
  const paDriver =
    Number(formData.liab_pa_driver ?? formData.paPaidDriver) || 0;
  const llPaidDriver =
    Number(formData.ll_paid_driver ?? formData.llPaidDriver) || 0;
  const llEmployees =
    Number(formData.ll_employees ?? formData.llEmployees) || 0;
  const netLiabilityBRaw = formData.net_liability_B || 0;

  const liabilityValues = isOwnDamageOnly
    ? {
        thirdPartyBase: 0,
        biFuelKit: 0,
        paOwner: 0,
        paPassenger: 0,
        paDriver: 0,
        llPaidDriver: 0,
        llEmployees: 0,
        netLiabilityB: 0,
      }
    : {
        thirdPartyBase,
        biFuelKit: Number(formData.liab_bifuel) || 0,
        paOwner,
        paPassenger,
        paDriver,
        llPaidDriver,
        llEmployees,
        netLiabilityB: netLiabilityBRaw,
      };

  const totalPremiumAB = odValues.netODPremium + liabilityValues.netLiabilityB;
  const sgstValue = formData.taxes?.sgst ?? Math.round(totalPremiumAB * 0.09);
  const cgstValue = formData.taxes?.cgst ?? Math.round(totalPremiumAB * 0.09);
  const gstAmount = sgstValue + cgstValue;
  const grossPremium = P.grossPremium ?? totalPremiumAB + gstAmount;

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  let headerY = 10;

  doc.addImage(
    beReliableLogo,
    "JPEG",
    20,
    headerY,
    beLogoWidth,
    beLogoHeight,
    "",
    "MEDIUM"
  );

  if (selectedCompanyLogo && companyLogoData) {
    doc.addImage(
      companyLogoData,
      "JPEG",
      pageWidth - marginRight - calculatedInsurerLogoWidth,
      headerY,
      calculatedInsurerLogoWidth,
      insurerLogoHeight,
      "",
      "MEDIUM"
    );
  }

  const headerBottomY = headerY + beLogoHeight + 12;
  yPos = headerBottomY;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("QUOTATION FOR CAR INSURANCE", pageWidth / 2, yPos, { align: "center" });

  yPos += 20;

  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();

  const sequence = String(Date.now() % 100).padStart(2, "0");
  const defaultQuoteNo = `BRQ/1508/${day}-${month}/${sequence}`;
  const defaultQuoteDate = `${day}-${month}-${year}`;

  const quoteNo = formData.quoteMeta?.quoteNo || defaultQuoteNo;
  const quoteDate = formData.quoteMeta?.quoteDate || defaultQuoteDate;

  doc.text(`Quote No: ${quoteNo}`, marginLeft, yPos);
  const formatDateDMYHeader = (dateString) => {
    if (!dateString) return "-";
    const parts = dateString.split("-");
    if (parts.length !== 3) return dateString;
    const [yearPart, monthPart, dayPart] = parts;
    return `${dayPart}-${monthPart}-${yearPart}`;
  };

  const policyExpiryDisplay = formatDateDMYHeader(V.policyExpiryDate);
  doc.text(`Policy Expiry Date: ${policyExpiryDisplay}`, pageWidth - marginRight, yPos, { align: "right" });

  yPos += 20;

  let quoteY = headerY + 25;
  const containerTopGap = 18;
  let customerHeadingY = quoteY + containerTopGap;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10, 35, 66);
  doc.text("Customer Details", marginLeft, yPos);
  doc.text(`Quote Date: ${quoteDate}`, pageWidth - marginRight, yPos, { align: "right" });
  doc.setTextColor(0, 0, 0);
  yPos += 6;

  doc.autoTable({
    startY: yPos,
    margin: { left: marginLeft, right: marginRight },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      cellPadding: 8,
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 8,
      minCellHeight: 22,
      lineWidth: 0.5,
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 8,
      cellPadding: 8,
      minCellHeight: 22,
    },
    head: [["Name", "Mobile", "Email"]],
    body: [[formData.customerName || "", formData.customerMobile || "", formData.customerEmail || ""]],
    tableLineWidth: 0.5,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: contentWidth / 3 },
      1: { cellWidth: contentWidth / 3 },
      2: { cellWidth: contentWidth / 3 },
    },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  yPos += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10, 35, 66);
  doc.text("Vehicle Details", marginLeft, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 6;

  doc.autoTable({
    startY: yPos,
    margin: { left: marginLeft, right: marginRight },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      cellPadding: 6,
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 6,
      minCellHeight: 22,
      lineWidth: 0.5,
    },
    bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8, cellPadding: 6, minCellHeight: 22 },
    head: [["Make", "Model", "Variant", "Fuel"]],
    body: [[V.make || "", V.model || "", V.variant || "", V.fuelType || ""]],
    tableLineWidth: 0.5,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: contentWidth / 4 },
      1: { cellWidth: contentWidth / 4 },
      2: { cellWidth: contentWidth / 4 },
      3: { cellWidth: contentWidth / 4 },
    },
  });
  yPos = doc.lastAutoTable.finalY + 2;

  doc.autoTable({
    startY: yPos,
    margin: { left: marginLeft, right: marginRight },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      cellPadding: 6,
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 6,
      minCellHeight: 22,
      lineWidth: 0.5,
    },
    bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8, cellPadding: 6, minCellHeight: 22 },
    head: [["Registration No.", "Manufacturing Year", "Cubic Capacity", "Seating Capacity"]],
    body: [[V.registrationNumber || "", V.registrationYear || "", V.cubicCapacity || "", V.seatingCapacity || ""]],
    tableLineWidth: 0.5,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: contentWidth / 4 },
      1: { cellWidth: contentWidth / 4 },
      2: { cellWidth: contentWidth / 4 },
      3: { cellWidth: contentWidth / 4 },
    },
  });
  yPos = doc.lastAutoTable.finalY + 2;

  const hypothecationLeaseValue = V.hypothecationLease || "-";

  doc.autoTable({
    startY: yPos,
    margin: { left: marginLeft, right: marginRight },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      cellPadding: 6,
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 6,
      minCellHeight: 22,
      lineWidth: 0.5,
    },
    bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8, cellPadding: 6, minCellHeight: 22 },
    head: [["Hypothecation / Lease", "City", "Zone", "Vehicle Age"]],
    body: [[hypothecationLeaseValue, V.city || "", V.zone || "", V.vehicleAge !== undefined ? `${V.vehicleAge}` : ""]],
    tableLineWidth: 0.5,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: contentWidth / 4 },
      1: { cellWidth: contentWidth / 4 },
      2: { cellWidth: contentWidth / 4 },
      3: { cellWidth: contentWidth / 4 },
    },
  });
  yPos = doc.lastAutoTable.finalY + 2;

  const elecAccValue = I.electrical === undefined || I.electrical === null || I.electrical === "" ? 0 : I.electrical;
  const nonElecAccValue = I.nonElectrical === undefined || I.nonElectrical === null || I.nonElectrical === "" ? 0 : I.nonElectrical;
  const biFuelValue = I.biFuel === undefined || I.biFuel === null || I.biFuel === "" ? 0 : I.biFuel;

  doc.autoTable({
    startY: yPos,
    margin: { left: marginLeft, right: marginRight },
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      lineWidth: 0.5,
      lineColor: [0, 0, 0],
      cellPadding: 6,
      minCellHeight: 22,
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 6,
      minCellHeight: 22,
      lineWidth: 0.5,
    },
    bodyStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 8, cellPadding: 6, minCellHeight: 22 },
    head: [["Vehicle IDV", "Elec Acc", "Non-Elec Acc", "Bi-Fuel", "Total IDV"]],
    body: [[I.vehicleIDV || "", elecAccValue, nonElecAccValue, biFuelValue, I.totalIDV || ""]],
    tableLineWidth: 0.5,
    theme: "grid",
    columnStyles: {
      0: { cellWidth: contentWidth / 5 },
      1: { cellWidth: contentWidth / 5 },
      2: { cellWidth: contentWidth / 5 },
      3: { cellWidth: contentWidth / 5 },
      4: { cellWidth: contentWidth / 5 },
    },
  });
  yPos = doc.lastAutoTable.finalY + 10;

  yPos += 12;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10, 35, 66);
  doc.text("Premium Summary", marginLeft, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 6;

  const premiumStartY = yPos;

  const leftTableWidth = 277;
  const rightTableWidth = 277;

  doc.autoTable({
    startY: premiumStartY,
    margin: { left: marginLeft },
    tableWidth: 197 + 77,
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      fontStyle: "normal",
      textColor: [0, 0, 0],
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      lineColor: [50, 50, 50],
      valign: "middle",
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      valign: "middle",
    },
    bodyStyles: {
      font: "helvetica",
      fontSize: 8,
      fontStyle: "normal",
      textColor: [0, 0, 0],
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      valign: "middle",
    },
    head: [["Own Damage Premium (A)", "Amount (INR)"]],
    body: [
      ["Basic OD Premium (Vehicle)", formatCurrency(odValues.basicOD)],
      ["Elec. Accessories", formatCurrency(odValues.elecAcc)],
      ["Non-Elec. Accessories", formatCurrency(odValues.nonElecAcc)],
      ["Bi-Fuel Kit", formatCurrency(odValues.biFuelKitOD)],
      ["Sub Total Basic Premium", formatCurrency(odValues.subTotalBasic)],
      ["Anti-Theft Device", formatCurrency(odValues.antiTheft)],
      [`No Claim Bonus (${formData.ncb || 0}%)`, formatCurrency(odValues.ncbAmount)],
      ["Sub Total (Deductibles)", formatCurrency(odValues.subTotalDiscounts)],
      ["Total OD Premium", formatCurrency(odValues.totalODPremium)],
      ["Add-on Amount", formatCurrency(odValues.addOnAmount)],
      ["Net OD Premium (A)", formatCurrency(odValues.netODPremium)],
    ],
    tableLineWidth: 0.6,
    tableLineColor: [50, 50, 50],
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 197, lineWidth: 0.6, halign: "left" },
      1: { cellWidth: 77, lineWidth: 0, halign: "center" },
    },
    didParseCell: function (data) {
      data.cell.styles.lineWidth = 0.6;
      if (data.section === "head" && data.column.index === 1) {
        data.cell.styles.halign = "center";
      }
      if (data.row.index >= 0 && [0, 4, 7, 8, 10].includes(data.row.index)) {
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  const leftTableEnd = doc.lastAutoTable.finalY;

  const totalWidth = 195.5 + 77;
  const rightColumnX = marginLeft + totalWidth;

  doc.autoTable({
    startY: premiumStartY,
    margin: { left: rightColumnX },
    tableWidth: totalWidth,
    pageBreak: "avoid",
    rowPageBreak: "avoid",
    styles: {
      font: "helvetica",
      fontSize: 8,
      fontStyle: "normal",
      textColor: [0, 0, 0],
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      lineColor: [50, 50, 50],
      valign: "middle",
    },
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 8,
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      valign: "middle",
    },
    bodyStyles: {
      font: "helvetica",
      fontSize: 8,
      fontStyle: "normal",
      textColor: [0, 0, 0],
      cellPadding: 4,
      minCellHeight: 22,
      lineWidth: 0.6,
      valign: "middle",
    },
    head: [["Liability Premium (B)", "Amount (INR)"]],
    body: [
      ["Third Party Base Premium", formatCurrency(liabilityValues.thirdPartyBase)],
      ["Bi-Fuel Kit", formatCurrency(liabilityValues.biFuelKit)],
      ["PA Owner Driver", formatCurrency(liabilityValues.paOwner)],
      ["PA Unnamed Passenger", formatCurrency(liabilityValues.paPassenger)],
      ["PA Paid Driver", formatCurrency(liabilityValues.paDriver)],
      ["LL to Paid Driver", formatCurrency(liabilityValues.llPaidDriver)],
      ["LL to Employees", formatCurrency(liabilityValues.llEmployees)],
      ["Net Liability Premium (B)", formatCurrency(liabilityValues.netLiabilityB)],
      ["Total Premium (A+B)", formatCurrency(totalPremiumAB)],
      ["GST (SGST 9% + CGST 9%)", formatCurrency(gstAmount)],
      ["Gross Premium Paid", formatCurrency(grossPremium)],
    ],
    tableLineWidth: 0.6,
    tableLineColor: [50, 50, 50],
    theme: "grid",
    columnStyles: {
      0: { cellWidth: 195.5, lineWidth: 0.6 },
      1: { cellWidth: 77, lineWidth: 0.6, halign: "center" },
    },
    didParseCell: function (data) {
      data.cell.styles.lineWidth = 0.6;
      if (data.section === "head" && data.column.index === 1) {
        data.cell.styles.halign = "center";
      }
      if (data.row.index >= 0 && [0, 2, 3, 5, 7, 8, 9, 10].includes(data.row.index)) {
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  yPos = Math.max(leftTableEnd, doc.lastAutoTable.finalY) + 10;

  yPos += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10, 35, 66);
  doc.text("Add-on Covers:", marginLeft, yPos);
  doc.setTextColor(0, 0, 0);
  yPos += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);

  const addonLabels = {
    zeroDep: "Zero Depreciation",
    engineProtect: "Engine Protection",
    rti: "Return To Invoice (RTI)",
    consumable: "Consumable",
    tyre: "Tyre Protection",
    keyProtect: "Key Protection",
    rodent: "Rodent-bite",
    personalBelonging: "Personal Belonging",
    emergency: "Emergency Transport",
    hotel: "Hotel Expenses",
    rsa: "Road Side Assistance",
  };

  const addonKeyMap = {
    zeroDep: "zeroDepreciation",
    engineProtect: "engineProtect",
    rti: "rti",
    consumable: "consumables",
    tyre: "tyreProtect",
    keyProtect: "keyProtect",
    rodent: "rodentDamage",
    personalBelonging: "personalBelongings",
    emergency: "emergencyHotelExpenses",
    hotel: "emergencyHotelExpenses",
    rsa: "roadSideAssistance",
  };

  const selectedAddons = Object.keys(addonLabels)
    .filter((key) => {
      const mappedKey = addonKeyMap[key];
      const addonEntry = mappedKey ? formData.addOns?.[mappedKey] : undefined;
      return addonEntry?.included === true;
    })
    .map((key) => addonLabels[key]);

  doc.text(selectedAddons.length > 0 ? selectedAddons.join(", ") : "", marginLeft, yPos, {
    maxWidth: contentWidth,
  });
  const addOnFinalY = yPos + 12;

  const importantNotesY = addOnFinalY + 20;
  yPos = importantNotesY;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Important Notes:", marginLeft, yPos);
  yPos += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const notes = [
    "This is a system-generated quotation.",
    "In case of any claim, NCB will be revised.",
    "All details are based on customer declaration.",
    "Taxes as per government norms.",
  ];
  const validUptoDate = new Date();
  validUptoDate.setDate(validUptoDate.getDate() + 7);
  const validUptoDay = String(validUptoDate.getDate()).padStart(2, "0");
  const validUptoMonth = String(validUptoDate.getMonth() + 1).padStart(2, "0");
  const validUptoYear = validUptoDate.getFullYear();
  const quoteValidUpto = `${validUptoDay}-${validUptoMonth}-${validUptoYear}`;

  notes.forEach((note, index) => {
    if (index === notes.length - 1) {
      doc.text(`• ${note}`, marginLeft, yPos);
      doc.setFont("helvetica", "bold");
      doc.text(`Quote Valid Upto: ${quoteValidUpto}`, pageWidth - marginRight, yPos, { align: "right" });
      doc.setFont("helvetica", "normal");
      yPos += 12;
    } else {
      doc.text(`• ${note}`, marginLeft, yPos);
      yPos += 12;
    }
  });

  const footerY = doc.internal.pageSize.getHeight() - 12;

  const tagline = "Be Reliable, Be Secure";
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");

  const taglineX = pageWidth / 2 - doc.getTextWidth(tagline) / 2;

  doc.text(tagline, taglineX, footerY - 28);

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");

  doc.text("Email: insurance@bereliable.in", marginLeft, footerY);

  const phoneText = "Phone: +91 70417 11159, +91 88661 41159";
  const phoneX = pageWidth / 2 - doc.getTextWidth(phoneText) / 2;
  doc.text(phoneText, phoneX, footerY);

  const websiteText = "Website: www.bereliable.in";
  const rightX = pageWidth - doc.getTextWidth(websiteText) - marginRight;
  doc.text(websiteText, rightX, footerY);

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const innerPageWidth = doc.internal.pageSize.getWidth();
    const innerPageHeight = doc.internal.pageSize.getHeight();
    doc.setDrawColor(138, 138, 138);
    doc.setLineWidth(1.5);
    doc.rect(8, 8, innerPageWidth - 16, innerPageHeight - 16);
  }

  doc.saveGraphicsState();
  doc.setGState(new doc.GState({ opacity: 0.21 }));

  doc.setFont("helvetica", "bold");
  doc.setFontSize(48);
  doc.setTextColor(180, 180, 180);

  const startX = 0;
  const startY = 320;

  const endX = 250;
  const endY = 880;

  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  doc.text("Be Reliable, Be Secure", midX, midY, {
    angle: 45,
    align: "justify",
  });

  doc.restoreGraphicsState();

  const filename = `BeReliable-Quotation-${formData.customerName || "Customer"}.pdf`;
  doc.save(filename);

  return doc;
};

